import { useEffect, useRef, useState } from "react";

interface BasePhotoPickerProps {
  file: File | null;
  onChange: (file: File | null) => void;
  /** Mirrors the server limit, so the operator is told before a failed upload. */
  maxBytes?: number;
}

const DEFAULT_MAX_BYTES = 15 * 1024 * 1024;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/**
 * Base photo of the registration (FIQ-1 §12, "Foto geral").
 *
 * Optional by design: the quick registration must stay quick, and refusing to
 * register a tree because nobody had a photo at hand would defeat the point of
 * FIQ-1. The photo is uploaded after the tree exists, because it is stored
 * against the tree id.
 */
export default function BasePhotoPicker({
  file,
  onChange,
  maxBytes = DEFAULT_MAX_BYTES,
}: BasePhotoPickerProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    // Revoked on replacement/unmount: object URLs hold the blob in memory
    // until released.
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function handleSelect(next: File | null) {
    setError(null);
    if (!next) {
      onChange(null);
      return;
    }
    if (!next.type.startsWith("image/")) {
      setError("Selecione um arquivo de imagem.");
      return;
    }
    if (next.size > maxBytes) {
      setError(`Imagem acima do limite de ${formatSize(maxBytes)}.`);
      return;
    }
    onChange(next);
  }

  return (
    <div className="space-y-2">
      <span className="text-xs font-medium text-foreground-700">
        Foto geral do exemplar
      </span>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleSelect(e.target.files?.[0] ?? null)}
      />

      {preview ? (
        <div className="flex items-start gap-3 rounded-lg border border-background-200 p-3">
          <img
            src={preview}
            alt="Pré-visualização da foto do exemplar"
            className="w-24 h-24 object-cover rounded-lg border border-background-200"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground-900 truncate">{file?.name}</p>
            <p className="text-[11px] text-foreground-500 mt-0.5">
              {file ? formatSize(file.size) : ""}
            </p>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => inputRef.current?.click()}
                className="text-xs text-primary-600 hover:underline cursor-pointer"
              >
                Trocar
              </button>
              <button
                onClick={() => {
                  handleSelect(null);
                  if (inputRef.current) inputRef.current.value = "";
                }}
                className="text-xs text-danger-600 hover:underline cursor-pointer"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 px-3 py-4 rounded-lg border border-dashed border-background-300 text-sm text-foreground-500 hover:bg-background-50 cursor-pointer"
        >
          <i className="ri-camera-line" />
          Adicionar foto
        </button>
      )}

      {error && <p className="text-xs text-danger-600">{error}</p>}

      <p className="text-[11px] text-foreground-400">
        Opcional. Enviada logo após o cadastro, com hash SHA-256 calculado no
        navegador — o servidor recusa o arquivo se ele não chegar idêntico.
      </p>
    </div>
  );
}
